import { Store } from 'components/common/types/Store.types';
import usePrevious from 'customHooks/usePrevious';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addToastNotification } from 'store/notification/reducer';
import { changeDealCurrentLineData } from 'store/dealManagement/reducer';

const useMediaOwnerDspSync = ({
  autoSelectEligibleMediaOwners = true,
  enabled = true,
}: { autoSelectEligibleMediaOwners?: boolean; enabled?: boolean } = {}): void => {
  const dispatch = useDispatch();

  const { dsp } = useSelector((state: Store) => state.dealManagement.programmatic);
  const { dsps } = useSelector((state: Store) => state.publisher);
  const { mediaOwnerMeta } = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine);

  const hasVIOOHSelectFeatures = useSelector((state: Store) => state.reseller.isInternalPublisher);

  const previousDsp = usePrevious(dsp);

  const handleDspChange = (): void => {
    const shouldUpdateMediaOwnersSelection =
      Object.keys(dsps)?.length && (!mediaOwnerMeta?.length || (previousDsp && dsp?.code !== previousDsp.code));

    if (!shouldUpdateMediaOwnersSelection) return;

    const selectedDspEligibleMediaOwners = dsps[dsp?.code as keyof typeof dsps]?.mediaOwners || [];
    const unavailableMediaOwners =
      mediaOwnerMeta?.map(({ code }) => code).filter((code) => !selectedDspEligibleMediaOwners.includes(code)) || [];

    if (dsp && unavailableMediaOwners.length) {
      const mediaOwnersCodes = unavailableMediaOwners.join(' and ');
      const verb = unavailableMediaOwners.length > 1 ? 'are' : 'is';

      dispatch(
        addToastNotification({
          description: `${mediaOwnersCodes} ${verb} not supported by the selected DSP.`,
          timeout: 10000,
          type: 'Failure',
        }),
      );
    }

    const updatedMediaOwners = autoSelectEligibleMediaOwners
      ? selectedDspEligibleMediaOwners.map((code) => ({
          code,
          name: code,
          ...(hasVIOOHSelectFeatures ? { fee: undefined } : {}),
        }))
      : mediaOwnerMeta?.filter(({ code }) => !unavailableMediaOwners.includes(code)) || [];

    dispatch(changeDealCurrentLineData({ mediaOwnerMeta: updatedMediaOwners }));
  };

  useEffect(() => {
    if (!enabled) return;
    handleDspChange();
  }, [dsp, enabled]);
};

export default useMediaOwnerDspSync;
